import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/UI/Button/Button';
import PlacementSelector from '../../components/UI/PlacementSelector/PlacementSelector';
import QuoteRequestModal from '../../components/Quotes/QuoteRequestModal';
import quoteService from '../../services/quoteService';
import { toast } from 'react-toastify';
import VerifiedBadge from '../../components/UI/VerifiedBadge/VerifiedBadge';
import { useAuth } from '../../contexts/AuthContext';
import ImageViewer from '../../components/UI/ImageViewer/ImageViewer';
import './ArtworkDetailPage.v2.css';

const ArtworkDetailPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [artwork, setArtwork] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState(null);

    const fetchArtwork = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/artworks/${id}`);
            setArtwork(res.data);
            setSelectedImage(0);
        } catch (err) {
            console.error('Error fetching artwork:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchArtwork();
    }, [fetchArtwork]);

    const handleQuoteSubmit = async (quoteData) => {
        try {
            await quoteService.createQuote(quoteData);
            toast.success(t('quote_request_sent') || 'Solicitação enviada com sucesso!');
        } catch (error) {
            console.error(error);
            toast.error(t('quote_request_failed') || 'Erro ao enviar solicitação.');
            throw error;
        }
    };

    const formatPrice = (pricing) => {
        if (!pricing) return null;

        if (pricing.isPriceRange) {
            return (
                <div className="price-item">
                    <span className="price-label">{t('price_range')}:</span>
                    <span className="price-value">R$ {pricing.minPrice} - R$ {pricing.maxPrice}</span>
                </div>
            );
        }

        const prices = [];
        if (pricing.withColor) {
            prices.push(
                <div key="with-color" className="price-item">
                    <span className="price-label">{t('with_color')}:</span>
                    <span className="price-value">R$ {pricing.withColor}</span>
                </div>
            );
        }
        if (pricing.withoutColor) {
            prices.push(
                <div key="without-color" className="price-item">
                    <span className="price-label">{t('without_color')}:</span>
                    <span className="price-value">R$ {pricing.withoutColor}</span>
                </div>
            );
        }

        if (prices.length > 0) return prices;

        if (pricing.basePrice) {
            return (
                <div className="price-item">
                    <span className="price-label">{t('price_from')}:</span>
                    <span className="price-value">R$ {pricing.basePrice}</span>
                </div>
            );
        }

        return <p className="contact-price">{t('contact_for_quote')}</p>;
    };

    if (loading) {
        return (
            <div className="artwork-detail-page">
                <div className="loading-state">{t('loading')}</div>
            </div>
        );
    }

    if (error || !artwork) {
        return (
            <div className="artwork-detail-page">
                <div className="error-state">
                    <h2>{t('error')}</h2>
                    <p>{t('artwork_error')}</p>
                    <Button onClick={() => navigate(-1)}>{t('back_to_home')}</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="artwork-detail-page">
            <div className="detail-content">
                <div className="breadcrumb">
                    <button onClick={() => navigate(-1)}>
                        ← {t('back_to_home')}
                    </button>
                </div>

                <div className="detail-grid">
                    <div className="image-section">
                        <div className="main-image">
                            {artwork.isSold && (
                                <div className="sold-badge">VENDIDO</div>
                            )}
                            <div className="fullscreen-hint" onClick={() => setViewerImage((artwork.images && artwork.images.length > 0) ? artwork.images[selectedImage] : artwork.imageUrl)}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                </svg>
                            </div>
                            <img
                                src={(artwork.images && artwork.images.length > 0) ? artwork.images[selectedImage] : artwork.imageUrl}
                                alt={artwork.title || t('artwork_alt')}
                                onClick={() => setViewerImage((artwork.images && artwork.images.length > 0) ? artwork.images[selectedImage] : artwork.imageUrl)}
                                style={{ cursor: 'zoom-in' }}
                            />
                        </div>
                        {artwork.images && artwork.images.length > 1 && (
                            <div className="image-thumbnails">
                                {artwork.images.map((img, idx) => (
                                    <div
                                        key={idx}
                                        className={`thumbnail ${idx === selectedImage ? 'active' : ''}`}
                                        onClick={() => setSelectedImage(idx)}
                                        style={{ backgroundImage: `url(${img})` }}
                                        aria-label={t('thumbnail_alt', { index: idx + 1 })}
                                    >
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="info-section">
                        <div className="info-header">
                            <div className="title-row">
                                {artwork.title && <h1>{artwork.title}</h1>}
                                {artwork.isSold && <span className="sold-tag">VENDIDO</span>}
                            </div>
                            <span className="category-tag">
                                {t(`style_${artwork.category.toLowerCase().replace(/\s+/g, '_')}`) || artwork.category}
                            </span>
                        </div>

                        <div className="detail-card">
                            <div className="pricing-header-row">
                                <h3>{t('pricing_section')}</h3>
                                {artwork.isSold ? (
                                    <Button variant="outline" disabled>
                                        {t('artwork_sold') || 'Arte Vendida'}
                                    </Button>
                                ) : artwork.profile?.userId === user?.id ? (
                                    <Button variant="outline" onClick={() => navigate('/artworks/manage')}>
                                        {t('manage_this_artwork') || 'Gerenciar esta Arte'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        size="medium"
                                        onClick={() => setIsQuoteModalOpen(true)}
                                    >
                                        {t('interested_quote') || 'Tenho Interesse / Orçar'}
                                    </Button>
                                )}
                            </div>
                            <div className="price-details">
                                {formatPrice(artwork.pricing)}
                            </div>
                        </div>

                        {artwork.description && (
                            <div className="detail-card">
                                <h3>{t('artwork_description')}</h3>
                                <p className="description-text">{artwork.description}</p>
                            </div>
                        )}

                        {artwork.placements && artwork.placements.length > 0 && (
                            <div className="detail-card">
                                <h3>{t('suggested_placements')}</h3>
                                <PlacementSelector
                                    placements={artwork.placements}
                                    readOnly={true}
                                />
                            </div>
                        )}

                        <div
                            className="artist-card"
                            style={{
                                backgroundImage: artwork.profile?.coverPhoto
                                    ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.9)), url(${artwork.profile.coverPhoto})`
                                    : 'linear-gradient(135deg, rgba(217, 4, 41, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%)'
                            }}
                        >
                            <div className="artist-header-content">
                                <div className="artist-avatar-container">
                                    <img
                                        src={artwork.profile?.profilePicture || 'https://via.placeholder.com/150'}
                                        alt={artwork.profile?.artistName}
                                        className="artist-avatar-img"
                                    />
                                </div>
                                <div className="artist-info-reduced">
                                    <span className="by-label-small">{t('by') || 'por'}</span>
                                    <h4 className="artist-name-large">
                                        {artwork.profile?.artistName || artwork.profile?.user?.username}
                                        {artwork.profile?.isVerified && <VerifiedBadge size={18} />}
                                    </h4>
                                    <span className="artist-username-small">@{artwork.profile?.user?.username}</span>
                                </div>
                            </div>
                            <Button
                                variant="primary"
                                className="view-profile-btn"
                                onClick={() => navigate(`/profile/${artwork.profile?.user?.username}`)}
                            >
                                {t('view_profile') || 'Ver Perfil'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <QuoteRequestModal
                isOpen={isQuoteModalOpen}
                onClose={() => setIsQuoteModalOpen(false)}
                artistProfileId={artwork.profile?.id}
                artistName={artwork.profile?.artistName}
                initialArtwork={artwork}
                onSubmit={handleQuoteSubmit}
            />

            <ImageViewer
                src={viewerImage}
                onClose={() => setViewerImage(null)}
                alt={artwork.title}
            />
        </div>
    );
};

export default ArtworkDetailPage;
